import { useState, useRef, useEffect } from "react";
import styles from "./index.module.scss";
import ContextMenu from "../context-menu";
import { HGHighlightText } from "../../../App/App";
import CheckIcon from "../../../icons/check.icon";
import { Spinner } from "react-bootstrap";

const IPAddressTable = ({ ipAddresses, onEdit, onDelete, searchValue, onClearSearch, allIpAddresses, isUpdatingIPAddress }) => {
  const [sortOrder, setSortOrder] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editError, setEditError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [optimisticUpdate, setOptimisticUpdate] = useState(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!isUpdatingIPAddress && updatingId !== null) {
      setUpdatingId(null);
      setOptimisticUpdate(null);
    }
  }, [isUpdatingIPAddress, updatingId]);

  const handleEdit = (id) => {
    const ip = ipAddresses.find((ip) => ip.Id === id);
    if (ip) {
      setEditingId(id);
      setEditValue(ip.IPAddress);
      setEditError("");
    }
  };

  const handleSave = (id) => {
    setEditError("");
    isSavingRef.current = true;
    const trimmedValue = editValue.trim();
    
    setUpdatingId(id);
    setOptimisticUpdate({ id, newValue: trimmedValue });
    
    const result = onEdit(id, editValue);
    if (result && result.success) {
      setEditingId(null);
      setEditValue("");
      setEditError("");
    } else {
      setEditError(result?.error || "Invalid IP address");
      setUpdatingId(null);
      setOptimisticUpdate(null);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 100);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue("");
    setEditError("");
    setUpdatingId(null);
    setOptimisticUpdate(null);
  };

  const handleKeyDown = (e, id) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSave(id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancel();
    }
  };

  const handleDelete = (id) => {
    onDelete(id);
    if (editingId === id) {
      setEditingId(null);
      setEditValue("");
    }
  };

  const handleSort = () => {
    setSortOrder((prev) => prev === 0 ? 1 : 0);
  };

  const ipAddressesWithOptimistic = ipAddresses.map((ip) => {
    if (optimisticUpdate && optimisticUpdate.id === ip.Id) {
      return { ...ip, IPAddress: optimisticUpdate.newValue };
    }
    return ip;
  });

  const sortedIpAddresses = [...ipAddressesWithOptimistic];
  if (sortOrder === 0) {
    sortedIpAddresses.sort((a, b) => {
      const aAddress = a.IPAddress || '';
      const bAddress = b.IPAddress || '';
      return aAddress.localeCompare(bAddress);
    });
  } else if (sortOrder === 1) {
    sortedIpAddresses.sort((a, b) => {
      const aAddress = a.IPAddress || '';
      const bAddress = b.IPAddress || '';
      return bAddress.localeCompare(aAddress);
    });
  }

  const isSearching = searchValue && searchValue.trim() !== "";
  const hasNoResults = (!ipAddresses || ipAddresses.length === 0);
  const hasOriginalData = allIpAddresses && allIpAddresses.length > 0;

  if (hasNoResults) {
    return (
      <div className={styles.rangesTableContainer}>
        <div className={styles.rangesTable}>
          <div className={styles.emptyState}>
            {isSearching && hasOriginalData ? (
              <p className={styles.emptyStateText}>
                <span>No results for "{searchValue}"</span>
                <span>
                  <button
                    onClick={onClearSearch}
                    className={styles.clearSearchButton}
                    type="button"
                  >
                    Clear search
                  </button>
                </span>
              </p>
            ) : (
              <p className={styles.emptyStateText}>
                <span>You don't have any IP addresses in your list</span>
                <span>You can add them above</span>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.rangesTableContainer}>
      <div className={styles.rangesTable}>
        <div className={styles.tableHeader}>
          <div className={styles.tableHeaderCell}>
            <button
              className={styles.sortButton}
              onClick={handleSort}
              aria-label="Sort by IP address"
            >
              <div className={styles.sortButtonText}>
                <span className={styles.sortButtonLabel}>IP address</span>
              </div>

              <div className={styles.sortButtonIcon}>
                <img 
                  src="/images/actioncenter/arrow2.svg"
                  style={{
                    width: '10px',
                    height: '10px',
                    transform: sortOrder === 0 
                      ? 'rotate(90deg)' 
                      : sortOrder === 1 
                      ? 'rotate(270deg)' 
                      : 'rotate(90deg)',
                    transition: 'transform 0.2s ease',
                  }}
                  alt="Sort arrow"
                />
              </div>
            </button>
          </div>
        </div>

        {sortedIpAddresses.map((ip) => (
          <div key={ip.Id} className={styles.tableRow}>
            <div className={styles.tableRowContent}>
              <div className={styles.tableRowTextWrapper}>
                <div className={styles.tableRowTextContainer}>
                  {editingId === ip.Id ? (
                    <div className={styles.editInputWrapper}>
                      <div className={styles.editInputContainer}>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => {
                            setEditValue(e.target.value);
                            setEditError("");
                          }}
                          onBlur={() => {
                            if (!isSavingRef.current) {
                              handleCancel();
                            }
                          }}
                          onKeyDown={(e) => handleKeyDown(e, ip.Id)}
                          className={`${styles.editInput} ${editError ? styles.editInputError : ''}`}
                          autoFocus
                        />
                        <div
                          className={styles.customCheckboxButton}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSave(ip.Id);
                          }}
                        >
                          <CheckIcon className={styles.checkmarkIcon} />
                        </div>
                      </div>
                      {editError && (
                        <div className={styles.editErrorMessage}>
                          {editError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.tableRowTextContainer}>
                      <span 
                        className={styles.tableRowText}
                        dangerouslySetInnerHTML={{
                          __html: HGHighlightText(ip.IPAddress || '', searchValue || '')
                        }}
                      />
                      {isUpdatingIPAddress && updatingId === ip.Id && (
                        <Spinner animation="border" size="sm" className={styles.updateSpinner} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {editingId !== ip.Id && (
              <div style={{ position: "relative" }}>
                <ContextMenu
                  onEdit={() => handleEdit(ip.Id)}
                  onDelete={() => handleDelete(ip.Id)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IPAddressTable;

